// Khoản trừ phí đào tạo Master Class trên phiếu lương (QĐ 0298, màn mẫu CEO
// duyệt 05/10/2026). Máy chủ đã tính xong — ở đây chỉ đổi con số thành chữ.
//
// Số tiền của khoản này ĐÃ nằm trong `customDeductions`; ô "Trừ tự do" phải
// trừ nó ra, nếu không cùng một khoản đứng hai dòng trên phiếu.
import type { components } from "@/lib/api/generated/schema";
import { vnd } from "./constants";
import type { PayrollRecord } from "./lib";

export type TrainingFeeItem = components["schemas"]["PayrollTrainingFeeItem"];
export type TrainingFeeTracked = components["schemas"]["PayrollTrainingFeeTracked"];

export const TRAINING_FEE_KIND = "training_fee";
export const TRAINING_FEE_LABEL = "Phí đào tạo Master Class";
/** Bồi thường khiếu nại (QĐ 0384) — đi CHUNG bước trừ góp với phí đào tạo. */
export const COMPENSATION_KIND = "complaint_compensation";

/** Các khoản trừ góp có dòng riêng trên phiếu — danh sách khoản trừ tay bỏ qua. */
export const INSTALLMENT_KINDS: readonly string[] = [TRAINING_FEE_KIND, COMPENSATION_KIND];

/** Tên khoản trên phiếu. Phiếu chốt trước QĐ 0384 không mang tên ⇒ phí đào tạo. */
export function installmentLabel(item: TrainingFeeItem): string {
  return item.label || TRAINING_FEE_LABEL;
}

export function trainingFeesOf(record: PayrollRecord): TrainingFeeItem[] {
  return record.trainingFees ?? [];
}

export function trainingFeeTotal(record: PayrollRecord): number {
  return trainingFeesOf(record).reduce((sum, item) => sum + (item.amount ?? 0), 0);
}

/** Khoản trừ nhập tay, KHÔNG gồm phí đào tạo. */
export function otherDeductionsOf(record: PayrollRecord): number {
  return Math.max((record.customDeductions ?? 0) - trainingFeeTotal(record), 0);
}

/** Nhãn nhỏ cạnh tên khoản: "kỳ 2/6", hoặc "trừ bù" khi lịch đã hết mà còn nợ. */
export function trainingFeeTag(item: TrainingFeeItem): string {
  if (item.installment > item.installments) return "trừ bù";
  return `kỳ ${item.installment}/${item.installments}`;
}

/** Lời giải thích dưới dòng, cho người bị trừ đọc trong "Lương của tôi". */
export function trainingFeeHint(item: TrainingFeeItem): string {
  const left = `còn lại ${vnd(item.remainingAfter)}`;
  if (item.finalPeriod) {
    return item.remainingAfter > 0
      ? `Kỳ lương cuối · ${left} chưa trừ được`
      : "Kỳ lương cuối · đã trừ hết";
  }
  if (item.capped) {
    const carried = `${vnd(item.carried ?? 0)} dồn sang tháng sau`;
    return item.amount > 0
      ? `Tháng này chỉ trừ ${vnd(item.amount)} (không quá 30% thực lĩnh) · ${carried} · ${left}`
      : `Tháng này chưa trừ được · ${carried} · ${left}`;
  }
  return [item.reason, `còn lại sau kỳ này ${vnd(item.remainingAfter)}`].filter(Boolean).join(" · ");
}

function dayMonthYear(iso: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/** Lời giải thích cho người SOÁT lương trong "Chi tiết lương". */
export function trainingFeeAuditHint(item: TrainingFeeItem): string {
  if (!item.total) return trainingFeeHint(item);
  const day = dayMonthYear(item.receivedOn);
  const source = item.kind === COMPENSATION_KIND ? "Chốt bồi hoàn ngày" : "Master Class báo ngày";
  return [
    item.kind === COMPENSATION_KIND ? item.reason : "",
    day ? `${source} ${day}` : "",
    `tổng ${vnd(item.total)}`,
    `đã trừ ${vnd(item.total - item.remainingAfter)}`,
    item.carried ? `${vnd(item.carried)} dồn sang tháng sau` : "",
    item.finalPeriod ? "kỳ lương cuối" : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

export type TrackedTone = "wait" | "ok" | "bad" | "off";

/** Nhãn + màu trạng thái của một dòng trên bảng theo dõi. */
export function trackedStatus(row: TrainingFeeTracked): { label: string; tone: TrackedTone } {
  switch (row.status) {
    case "done":
      return { label: "Đã trừ xong", tone: "ok" };
    case "left_owing":
      return { label: "Đã nghỉ việc — còn nợ, xử lý tay", tone: "bad" };
    case "scheduled":
      return { label: `Bắt đầu trừ từ ${row.firstMonth}`, tone: "wait" };
    case "unmatched":
      return { label: "Chưa nối được nhân sự — chưa trừ", tone: "bad" };
    default:
      return { label: "Đang trừ", tone: "wait" };
  }
}
